import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSession } from '../lib/session'
import { useMembers, useRefreshingMutation, useTasks } from '../lib/queries'
import { api } from '../lib/api'
import type { Household, Member, Task } from '../../shared/types'
import { Avatar, Empty, ErrorText, Loading, MIN_PASSWORD_LENGTH, PasswordHint, Pill, Sheet } from '../components/ui'
import { PlusIcon } from '../components/Icons'
import { EmojiPicker } from '../components/EmojiPicker'
import { KidLoginForm } from '../components/KidLoginForm'


export function Settings() {
  const navigate = useNavigate()
  const { household, isParent } = useSession()
  const { data: members } = useMembers()
  const { data: tasks } = useTasks()
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [pinOpen, setPinOpen] = useState(false)
  const [addTaskOpen, setAddTaskOpen] = useState(false)

  if (!isParent) {
    return (
      <div className="page">
        <Empty title="Parents only" hint="Ask a parent to change the household settings." />
      </div>
    )
  }
  if (!household) return <Loading />

  return (
    <div className="page">
      <div className="row-between">
        <h1 className="title">Settings</h1>
        <button type="button" className="btn btn-quiet" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <HouseholdCard household={household} />

      <section className="card">
        <div className="row-between">
          <h2 className="title">Family</h2>
          <button type="button" className="btn btn-ghost" onClick={() => setAddMemberOpen(true)}>
            <PlusIcon /> Add
          </button>
        </div>
        <div className="list">
          {(members ?? []).map((member) => (
            <button
              key={member.id}
              type="button"
              className="check"
              onClick={() => setEditingMember(member)}
            >
              <Avatar emoji={member.emoji} color={member.color} ring />
              <span className="check-body">
                <span className="check-title">{member.name}</span>
                <span className="check-detail">
                  {member.role === 'parent' ? 'Parent' : member.username ? `Kid · ${member.username}` : 'Kid'}
                </span>
              </span>
              {member.role === 'kid' && !member.username ? <Pill tone="warn">No login yet</Pill> : null}
            </button>
          ))}
        </div>
      </section>

      <InviteSettings />

      <DeviceSettings />

      <TaskSettings tasks={tasks ?? []} onAdd={() => setAddTaskOpen(true)} />

      <section className="card">
        <h2 className="title">Parent PIN</h2>
        <p className="subtitle">
          Another parent needs it once, when they use an invite code to set up their own login. Not the
          same as your account password.
        </p>
        <button type="button" className="btn btn-ghost btn-block" onClick={() => setPinOpen(true)}>
          Change PIN
        </button>
      </section>

      <AccountSettings />

      <MemberSheet member={editingMember} onClose={() => setEditingMember(null)} />
      <AddMemberSheet open={addMemberOpen} onClose={() => setAddMemberOpen(false)} />
      <AddTaskSheet open={addTaskOpen} onClose={() => setAddTaskOpen(false)} />
      <PinSheet open={pinOpen} onClose={() => setPinOpen(false)} />
    </div>
  )
}

function InviteSettings() {
  const { members } = useSession()
  const { data: invites } = useQuery({ queryKey: ['invites'], queryFn: api.invites })
  const [created, setCreated] = useState<{ code: string; expiresAt: string } | null>(null)
  const [memberId, setMemberId] = useState<string | null>(null)

  const create = useRefreshingMutation(() =>
    api.createInvite({ role: 'parent', memberId, hours: 24, maxUses: 1 }),
  )
  const revoke = useRefreshingMutation((code: string) => api.revokeInvite(code))

  const candidates = members.filter((m) => m.role === 'parent')

  return (
    <section className="card">
      <h2 className="title">Invite another parent</h2>
      <p className="subtitle">
        They use the code once, then add their own email and password. Codes last 24 hours and work a
        single time. Kids don't need a code: give them a login from their profile above.
      </p>

      <div className="field">
        <span className="label">Lock it to one person (optional)</span>
        <div className="chips">
          <button
            type="button"
            className={`chip${memberId === null ? ' chip-on' : ''}`}
            onClick={() => setMemberId(null)}
          >
            Anyone
          </button>
          {candidates.map((member) => (
            <button
              key={member.id}
              type="button"
              className={`chip${memberId === member.id ? ' chip-on' : ''}`}
              onClick={() => setMemberId(member.id)}
            >
              {member.emoji} {member.name}
            </button>
          ))}
        </div>
      </div>

      <ErrorText error={create.error} />
      <button
        type="button"
        className="btn btn-primary btn-block"
        disabled={create.isPending}
        onClick={() => create.mutate(undefined, { onSuccess: (result) => setCreated(result) })}
      >
        Create an invite code
      </button>

      {created ? (
        <div className="banner banner-ok">
          <div className="grow">
            <div className="banner-title">Read this out or text it over</div>
            <div
              className="tabular"
              style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '0.18em', margin: '6px 0' }}
            >
              {created.code}
            </div>
            <p className="subtitle">
              They open the app, tap "I have an invite code", and enter it. Expires{' '}
              {new Date(created.expiresAt).toLocaleString([], {
                weekday: 'short',
                hour: 'numeric',
                minute: '2-digit',
              })}
              .
            </p>
          </div>
        </div>
      ) : null}

      {invites && invites.length > 0 ? (
        <>
          <hr className="divider" />
          <p className="section-title">Codes still live</p>
          <div className="list">
            {invites.map((invite) => (
              <div key={invite.code} className="row-between">
                <div>
                  <span className="tabular" style={{ fontWeight: 700, letterSpacing: '0.1em' }}>
                    {invite.code}
                  </span>
                  <div className="faint">
                    {invite.role === 'kid' ? 'Kid' : 'Parent'} · used {invite.uses}/{invite.maxUses}
                  </div>
                </div>
                <button type="button" className="btn btn-quiet" onClick={() => revoke.mutate(invite.code)}>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  )
}

function DeviceSettings() {
  const { data: devices } = useQuery({ queryKey: ['devices'], queryFn: api.devices })
  const revoke = useRefreshingMutation((id: string) => api.revokeDevice(id))

  return (
    <section className="card">
      <h2 className="title">Linked devices</h2>
      <p className="subtitle">
        Removing one signs it out immediately — useful for a lost phone, or one that's been handed on.
      </p>
      {!devices || devices.length === 0 ? (
        <Empty title="No devices linked yet" />
      ) : (
        <div className="list">
          {devices.map((device) => (
            <div key={device.id} className="row-between">
              <div>
                <span style={{ fontWeight: 600 }}>
                  {device.label ?? 'A device'}
                  {device.isThisDevice ? ' · this one' : ''}
                </span>
                <div className="faint">
                  Last used{' '}
                  {device.lastSeenAt
                    ? new Date(device.lastSeenAt).toLocaleDateString([], { month: 'short', day: 'numeric' })
                    : 'never'}
                </div>
              </div>
              {device.isThisDevice ? (
                <Pill>In use</Pill>
              ) : (
                <button type="button" className="btn btn-quiet" onClick={() => revoke.mutate(device.id)}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ErrorText error={revoke.error} />
    </section>
  )
}

function AccountSettings() {
  const { session } = useSession()
  const [open, setOpen] = useState(false)

  if (!session?.hasAccount) return null

  return (
    <section className="card">
      <h2 className="title">Your login</h2>
      <p className="subtitle">The email and password you use to sign in and to recover the household.</p>
      <button type="button" className="btn btn-ghost btn-block" onClick={() => setOpen(true)}>
        Change password
      </button>
      <ChangePasswordSheet open={open} onClose={() => setOpen(false)} />
    </section>
  )
}

function ChangePasswordSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  const change = useRefreshingMutation(() => api.changePassword(current, next))

  return (
    <Sheet open={open} title="Change password" onClose={onClose}>
      <p className="subtitle">Every other device signed in to your account will be signed out.</p>
      <div className="field">
        <label className="label" htmlFor="cp-current">
          Current password
        </label>
        <input
          id="cp-current"
          className="input"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="cp-next">
          New password
        </label>
        <input
          id="cp-next"
          className="input"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        <PasswordHint password={next} />
      </div>
      <ErrorText error={change.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!current || next.length < MIN_PASSWORD_LENGTH || change.isPending}
        onClick={() =>
          change.mutate(undefined, {
            onSuccess: () => {
              setCurrent('')
              setNext('')
              onClose()
            },
          })
        }
      >
        Change password
      </button>
    </Sheet>
  )
}

function HouseholdCard({ household }: { household: Household }) {
  const [draft, setDraft] = useState(household)
  const save = useRefreshingMutation((input: Partial<Household>) => api.updateHousehold(input))
  const dirty = JSON.stringify(draft) !== JSON.stringify(household)

  return (
    <section className="card">
      <h2 className="title">Household</h2>

      <div className="field">
        <label className="label" htmlFor="hh-name">
          Name
        </label>
        <input
          id="hh-name"
          className="input"
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="hh-tz">
          Timezone
        </label>
        <input
          id="hh-tz"
          className="input"
          value={draft.timezone}
          onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
        />
      </div>

      <ErrorText error={save.error} />
      <button
        type="button"
        className="btn btn-primary btn-block"
        disabled={!dirty || save.isPending}
        onClick={() =>
          save.mutate({
            name: draft.name,
            timezone: draft.timezone,
          })
        }
      >
        Save household
      </button>
    </section>
  )
}

const CATEGORY_LABELS: Record<string, string> = {
  'personal-daily': 'Daily personal',
  'personal-weekly': 'Weekly personal',
  'household-daily': 'Daily household',
  'household-weekly': 'Weekly household',
  'reward-anytime': 'Reward jobs',
}

function TaskSettings({ tasks, onAdd }: { tasks: Task[]; onAdd: () => void }) {
  const toggle = useRefreshingMutation(({ id, active }: { id: string; active: boolean }) =>
    api.updateTask(id, { active }),
  )

  const groups = new Map<string, Task[]>()
  for (const task of tasks) {
    const key = `${task.category}-${task.cadence}`
    groups.set(key, [...(groups.get(key) ?? []), task])
  }

  return (
    <section className="card">
      <div className="row-between">
        <h2 className="title">Lists</h2>
        <button type="button" className="btn btn-ghost" onClick={onAdd}>
          <PlusIcon /> Add
        </button>
      </div>
      <p className="subtitle">Turn anything off that doesn’t apply. History is kept either way.</p>

      {[...groups.entries()].map(([key, groupTasks]) => (
        <div key={key} className="stack">
          <p className="section-title">{CATEGORY_LABELS[key] ?? key}</p>
          <div className="list">
            {groupTasks.map((task) => (
              <button
                key={task.id}
                type="button"
                className={`check${task.active ? '' : ' '}`}
                style={{ opacity: task.active ? 1 : 0.5 }}
                onClick={() => toggle.mutate({ id: task.id, active: !task.active })}
              >
                <span className="check-body">
                  <span className="check-title">{task.title}</span>
                  {task.detail ? <span className="check-detail">{task.detail}</span> : null}
                </span>
                <Pill tone={task.active ? 'ok' : 'default'}>{task.active ? 'On' : 'Off'}</Pill>
              </button>
            ))}
          </div>
        </div>
      ))}
      <ErrorText error={toggle.error} />
    </section>
  )
}

function MemberSheet({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('🙂')

  // Re-seed the form whenever a different member is opened.
  const [seededFor, setSeededFor] = useState<string | null>(null)
  if (member && seededFor !== member.id) {
    setSeededFor(member.id)
    setName(member.name)
    setEmoji(member.emoji)
  }

  const save = useRefreshingMutation((input: Partial<Member> & { id: string }) =>
    api.updateMember(input.id, input),
  )
  const remove = useRefreshingMutation((id: string) => api.deleteMember(id))

  return (
    <Sheet open={member !== null} title={member?.name ?? 'Member'} onClose={onClose}>
      {member ? (
        <>
          <div className="field">
            <label className="label" htmlFor="member-name">
              Name
            </label>
            <input
              id="member-name"
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <span className="label">Emoji</span>
            <EmojiPicker key={member.id} value={emoji} onChange={setEmoji} />
          </div>

          <ErrorText error={save.error ?? remove.error} />

          <button
            type="button"
            className="btn btn-primary btn-block btn-lg"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: member.id,
                  name: name.trim() || member.name,
                  emoji,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </button>

          {member.role === 'kid' ? <KidLoginForm key={member.id} member={member} /> : null}

          <button
            type="button"
            className="btn btn-danger btn-block"
            disabled={remove.isPending}
            onClick={() => remove.mutate(member.id, { onSuccess: onClose })}
          >
            Remove from household
          </button>
        </>
      ) : null}
    </Sheet>
  )
}

function AddMemberSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [role, setRole] = useState<'parent' | 'kid'>('kid')
  const [emoji, setEmoji] = useState('🙂')

  const create = useRefreshingMutation(() => api.createMember({ name: name.trim(), role, emoji }))

  return (
    <Sheet open={open} title="Add someone" onClose={onClose}>
      <div className="field">
        <label className="label" htmlFor="new-member-name">
          Name
        </label>
        <input
          id="new-member-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="field">
        <span className="label">Role</span>
        <div className="chips">
          {(['kid', 'parent'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${role === option ? ' chip-on' : ''}`}
              onClick={() => setRole(option)}
            >
              {option === 'parent' ? 'Parent' : 'Kid'}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="label">Emoji</span>
        <EmojiPicker value={emoji} onChange={setEmoji} />
      </div>
      <ErrorText error={create.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!name.trim() || create.isPending}
        onClick={() =>
          create.mutate(undefined, {
            onSuccess: () => {
              setName('')
              onClose()
            },
          })
        }
      >
        Add
      </button>
    </Sheet>
  )
}

function AddTaskSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState<Task['category']>('personal')
  const [cadence, setCadence] = useState<Task['cadence']>('daily')
  const [points, setPoints] = useState(1)

  const create = useRefreshingMutation(() =>
    api.createTask({
      title: title.trim(),
      category,
      cadence,
      points,
      appliesTo: category === 'household' ? 'everyone' : 'kid',
    }),
  )

  return (
    <Sheet open={open} title="Add to a list" onClose={onClose}>
      <div className="field">
        <label className="label" htmlFor="new-task-title">
          What is it?
        </label>
        <input
          id="new-task-title"
          className="input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      <div className="field">
        <span className="label">List</span>
        <div className="chips">
          {(['personal', 'household', 'reward'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${category === option ? ' chip-on' : ''}`}
              onClick={() => {
                setCategory(option)
                if (option === 'reward') setCadence('anytime')
                else if (cadence === 'anytime') setCadence('daily')
              }}
            >
              {option === 'personal' ? 'Personal' : option === 'household' ? 'Household' : 'Reward job'}
            </button>
          ))}
        </div>
      </div>
      {category !== 'reward' ? (
        <div className="field">
          <span className="label">How often</span>
          <div className="chips">
            {(['daily', 'weekly'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`chip${cadence === option ? ' chip-on' : ''}`}
                onClick={() => setCadence(option)}
              >
                {option === 'daily' ? 'Daily' : 'Weekly'}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="field">
        <span className="label">Points</span>
        <div className="chips">
          {[1, 2, 3, 5, 10].map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${points === option ? ' chip-on' : ''}`}
              onClick={() => setPoints(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <ErrorText error={create.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!title.trim() || create.isPending}
        onClick={() =>
          create.mutate(undefined, {
            onSuccess: () => {
              setTitle('')
              onClose()
            },
          })
        }
      >
        Add
      </button>
    </Sheet>
  )
}

function PinSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPin, setCurrentPin] = useState('')
  const [newPin, setNewPin] = useState('')

  const change = useRefreshingMutation(() => api.changePin(currentPin, newPin))

  return (
    <Sheet open={open} title="Change parent PIN" onClose={onClose}>
      <div className="field">
        <label className="label" htmlFor="pin-current">
          Current PIN
        </label>
        <input
          id="pin-current"
          className="input tabular"
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          value={currentPin}
          onChange={(event) => setCurrentPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="pin-new">
          New PIN
        </label>
        <input
          id="pin-new"
          className="input tabular"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          value={newPin}
          onChange={(event) => setNewPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
        />
      </div>
      <ErrorText error={change.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={currentPin.length < 4 || newPin.length < 4 || change.isPending}
        onClick={() =>
          change.mutate(undefined, {
            onSuccess: () => {
              setCurrentPin('')
              setNewPin('')
              onClose()
            },
          })
        }
      >
        Change PIN
      </button>
    </Sheet>
  )
}
